import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_REVIEWS = [
  {
    author_name: "Sarah M.",
    city: "Chandler",
    rating: 5,
    text: "Best dry cleaner in the East Valley. The pickup service is a game-changer — I haven't stepped into a cleaner in two years. Shirts come back perfectly pressed every time.",
    relative_time: "a month ago",
  },
  {
    author_name: "James R.",
    city: "Ahwatukee",
    rating: 5,
    text: "As a fellow veteran, I love supporting Oxford. Fair prices, friendly team and they actually care about your clothes. Wedding gown came out beautifully preserved.",
    relative_time: "2 months ago",
  },
  {
    author_name: "Priya K.",
    city: "Tempe",
    rating: 5,
    text: "Free pickup all the way to Tempe? Yes please. They text 30 minutes before showing up, totally seamless. My go-to for suits and silk blouses.",
    relative_time: "3 weeks ago",
  },
  {
    author_name: "Mark L.",
    city: "Gilbert",
    rating: 5,
    text: "I had a tough oil stain on a wool coat that two other cleaners couldn't get out. Oxford did it on the first try. Genuinely impressed.",
    relative_time: "2 weeks ago",
  },
  {
    author_name: "Dana T.",
    city: "Maricopa",
    rating: 5,
    text: "Long drive to Chandler used to be the only option. Now they pick up at my door in Maricopa — every week. Couldn't be happier.",
    relative_time: "a week ago",
  },
  {
    author_name: "Eddie V.",
    city: "Chandler",
    rating: 5,
    text: "Family-owned, friendly, and quick. They remember my name and my starch preference. That's small-town service in a big city.",
    relative_time: "a month ago",
  },
];

const DEFAULT_GOOGLE_URL =
  "https://www.google.com/maps/search/?api=1&query=Oxford+Cleaners+5865+W+Ray+Rd+Chandler+AZ";

// Inline Google "G" logo as SVG
const GoogleG = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.77c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.47 1.18 4.93l3.66-2.83z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"
    />
  </svg>
);

const StarsRow = ({ n = 5, size = "h-4 w-4" }) => (
  <div className="flex items-center gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={`star-${i}`}
        className={`${size} ${i < n ? "fill-amber-400 stroke-amber-400" : "stroke-slate-300"}`}
      />
    ))}
  </div>
);

export default function Testimonials() {
  const [live, setLive] = useState(null); // { rating, user_rating_count, google_maps_uri, reviews }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/google-reviews`);
        if (cancelled) return;
        if (data?.ok && Array.isArray(data.reviews) && data.reviews.length > 0) {
          setLive({
            rating: data.rating,
            user_rating_count: data.user_rating_count,
            google_maps_uri: data.google_maps_uri || DEFAULT_GOOGLE_URL,
            reviews: data.reviews,
          });
        }
      } catch (e) {
        // Silent — fallback reviews render instead.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const usingLive = Boolean(live);
  const reviews = usingLive ? live.reviews : FALLBACK_REVIEWS;
  const rating = usingLive && typeof live.rating === "number" ? live.rating.toFixed(1) : "4.9";
  const count = usingLive && live.user_rating_count ? live.user_rating_count : 480;
  const googleUrl = usingLive ? live.google_maps_uri : DEFAULT_GOOGLE_URL;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-20 lg:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-3 py-1.5"
              data-testid={usingLive ? "reviews-live-badge" : "reviews-fallback-badge"}
            >
              <GoogleG className="h-4 w-4" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">
                {usingLive ? "Live reviews on Google" : "Reviews on Google"}
              </span>
            </div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-navy">
              Neighbors who love their clean clothes.
            </h2>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <StarsRow size="h-5 w-5" />
              <span className="font-semibold text-brand-navy" data-testid="reviews-rating-value">
                {rating} out of 5
              </span>
              <span className="text-slate-500" data-testid="reviews-count-value">
                · {count.toLocaleString()}{usingLive ? "" : "+"} verified reviews
              </span>
            </div>
          </div>
          <Button
            asChild
            data-testid="reviews-see-all-btn"
            variant="outline"
            className="rounded-full border-slate-300 hover:border-brand-blue h-11 px-5 gap-2 self-start lg:self-end"
          >
            <a href={googleUrl} target="_blank" rel="noreferrer">
              <GoogleG className="h-4 w-4" />
              See all on Google
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        </div>

        <div
          className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid={usingLive ? "reviews-grid-live" : "reviews-grid-fallback"}
        >
          {loading && !usingLive
            ? Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  data-testid={`review-skeleton-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white p-7 animate-pulse"
                >
                  <div className="h-4 w-24 bg-slate-100 rounded mb-4" />
                  <div className="h-3 w-full bg-slate-100 rounded mb-2" />
                  <div className="h-3 w-11/12 bg-slate-100 rounded mb-2" />
                  <div className="h-3 w-8/12 bg-slate-100 rounded" />
                  <div className="mt-6 pt-5 border-t border-slate-200 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-slate-100" />
                    <div className="flex-1">
                      <div className="h-3 w-24 bg-slate-100 rounded mb-2" />
                      <div className="h-3 w-16 bg-slate-100 rounded" />
                    </div>
                  </div>
                </div>
              ))
            : reviews.map((r, i) => {
                const name = r.author_name || "Google user";
                const initial = (name || "?").trim()[0]?.toUpperCase() || "?";
                const meta = usingLive
                  ? r.relative_time || "Recent review"
                  : `${r.city || "Chandler"}, AZ · ${r.relative_time || ""}`.replace(/·\s*$/, "").trim();
                return (
                  <article
                    key={`${name}-${i}`}
                    data-testid={`review-card-${i}`}
                    className="lift relative rounded-2xl border border-slate-200 bg-white p-7"
                  >
                    <Quote className="h-7 w-7 text-brand-lightblue absolute top-5 right-5" />
                    <div className="mb-3">
                      <StarsRow n={typeof r.rating === "number" ? r.rating : 5} />
                    </div>
                    <p className="text-slate-700 leading-relaxed line-clamp-6">
                      &ldquo;{r.text}&rdquo;
                    </p>
                    <div className="mt-5 pt-5 border-t border-slate-200 flex items-center gap-3">
                      {r.author_photo_url ? (
                        <img
                          src={r.author_photo_url}
                          alt={name}
                          referrerPolicy="no-referrer"
                          className="h-10 w-10 rounded-full object-cover relative"
                          data-testid={`review-avatar-${i}`}
                        />
                      ) : (
                        <div
                          className="h-10 w-10 rounded-full bg-brand-lightblue grid place-items-center text-brand-blue font-display font-bold relative"
                          data-testid={`review-avatar-fallback-${i}`}
                        >
                          {initial}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-semibold text-brand-navy truncate">
                          {name}
                        </p>
                        <p className="text-sm text-slate-500 truncate">{meta}</p>
                      </div>
                      <GoogleG className="h-4 w-4 shrink-0" />
                    </div>
                  </article>
                );
              })}
        </div>
        {usingLive && (
          <p
            className="mt-8 text-xs text-slate-500 text-center"
            data-testid="reviews-attribution"
          >
            Reviews sourced directly from Google Business Profile · Updated daily
          </p>
        )}
      </div>
    </section>
  );
}
